import { useEffect, useRef } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";

import AdminLayout from "./layouts/AdminLayout";
import PublicLayout from "./layouts/PublicLayout";
import RequireAuth from "./components/RequireAuth";
import RequireRole from "./components/RequireRole";

import AuthCallback from "./routes/auth/AuthCallback";
import AuthSilentRenew from "./routes/auth/AuthSilentRenew";
import Home from "./routes/Home";
import TranslateLanding from "./routes/translate/TranslateLanding";
import TranslateEditor from "./routes/translate/TranslateEditor";
import MyTranslations from "./routes/translate/MyTranslations";
import AdminReviewList from "./routes/admin/AdminReviewList";
import AdminReviewFocus from "./routes/admin/AdminReviewFocus";
import AdminReviewBatch from "./routes/admin/AdminReviewBatch";
import AdminTerminology from "./routes/admin/AdminTerminology";
import AdminTerminologyItem from "./routes/admin/AdminTerminologyItem";
import AdminLlmJobs from "./routes/admin/AdminLlmJobs";
import AdminOverview from "./routes/admin/AdminOverview";
import AdminLlmJobDetail from "./routes/admin/AdminLlmJobDetail";
import AdminFeedback from "./routes/admin/AdminFeedback";
import AdminFeedbackDetail from "./routes/admin/AdminFeedbackDetail";
import SenseGroupedReview from "./routes/admin/SenseGroupedReview";
import AdminApiTokens from "./routes/admin/AdminApiTokens";
import AdminApiLog from "./routes/admin/AdminApiLog";
import AdminBackups from "./routes/admin/AdminBackups";
import Styleguide from "./routes/admin/Styleguide";
import NotFound from "./routes/NotFound";
import { registerAuthForFetch } from "./api/mtFetch";
import { useAuthWatchdog } from "./auth/useAuthWatchdog";

const TERMINOLOGY_REVIEWER_ROLE = "terminology_reviewer";

export default function App() {
  const auth = useAuth();

  // Сторож OIDC-сессии: догоняет упавший/пропущенный silent-renew и логирует
  // причину вылета. Без него один промах renew = моментальное «Войти».
  useAuthWatchdog(auth);

  // Токен держим в ref и обновляем во время РЕНДЕРА (синхронно), а getter
  // регистрируем один раз. Render идёт раньше эффектов дочерних компонентов,
  // поэтому к моменту, когда дочерний запрос (напр. /v1/backends) стреляет в
  // своём эффекте, tokenRef.current уже актуален. Раньше getter был замыканием
  // на auth.user из конкретного рендера: на перезагрузке страницы запрос успевал
  // уйти со «старым» (пустым) auth.user → Bearer не прикладывался → 401
  // «Сессия истекла», хотя токен в localStorage свежий.
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = auth.user?.access_token;
  useEffect(() => {
    registerAuthForFetch(() => tokenRef.current);
  }, []);

  return (
    <Routes>
      {/* OIDC callbacks — public, no auth wrap */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/silent-renew" element={<AuthSilentRenew />} />

      {/* Public landing — кнопка «Войти», без auto-redirect */}
      <Route path="/" element={<Home />} />

      {/* Translator workspace (any authenticated user) */}
      <Route
        element={
          <RequireAuth>
            <PublicLayout />
          </RequireAuth>
        }
      >
        <Route path="/translate" element={<TranslateLanding />} />
        <Route path="/translate/mine" element={<MyTranslations />} />
        <Route path="/translate/:draftId" element={<TranslateEditor />} />
        <Route
          path="/translate/:draftId/edit"
          element={<TranslateEditor initialMode="edit" />}
        />
      </Route>

      {/* Admin workspace (terminology_reviewer role required) */}
      <Route
        element={
          <RequireAuth>
            <RequireRole role={TERMINOLOGY_REVIEWER_ROLE} fallback="/translate">
              <AdminLayout />
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route path="/admin" element={<Navigate to="/admin/review" replace />} />
        <Route path="/admin/review" element={<AdminReviewList />} />
        <Route
          path="/admin/review/focus/:stableKey"
          element={<AdminReviewFocus />}
        />
        <Route path="/admin/review/batch" element={<AdminReviewBatch />} />
        <Route path="/admin/terminology" element={<AdminTerminology />} />
        <Route
          path="/admin/terminology/:itemId"
          element={<AdminTerminologyItem />}
        />
        <Route
          path="/admin/abbreviations/by-short/:short"
          element={<SenseGroupedReview />}
        />
        <Route path="/admin/styleguide" element={<Styleguide />} />
        <Route path="/admin/overview" element={<AdminOverview />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route
          path="/admin/feedback/:feedbackId"
          element={<AdminFeedbackDetail />}
        />
        <Route path="/admin/llm-jobs" element={<AdminLlmJobs />} />
        <Route
          path="/admin/llm-jobs/:batchJobId"
          element={<AdminLlmJobDetail />}
        />
        <Route path="/admin/api-tokens" element={<AdminApiTokens />} />
        <Route path="/admin/api-log" element={<AdminApiLog />} />
        <Route path="/admin/backups" element={<AdminBackups />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
